<template>
  <div class="filter-editor">
    <hr-filter-chip-group
      v-if="hasDateOperators"
      :model-value="dateOperator"
      :options="dateOperatorOptions"
      data-e2e="select-filter-date-operator"
      @update:model-value="onDateOperatorChange"
    />

    <hr-text-field
      v-if="isWeeksAgo"
      :model-value="weeks"
      mask="###"
      placeholder="12"
      :hint="i18n.t('common.filter.dateOperatorWeeksAgoHint')"
      data-e2e="input-filter-weeks-ago"
      @update:model-value="onWeeksChange"
    />

    <template v-else>
      <hr-list-select
        :model-value="modelValue.filterCriteriaType"
        :options="presetOptions"
        :label="i18n.t('history.filter.selectDateRangeLabel')"
        @update:model-value="onPresetChange"
      />

      <div
        v-if="isCustomDates"
        class="filter-editor__calendar"
      >
        <hr-date
          :model-value="calendarDates"
          multiple
          mask="YYYY-MM-DD"
          @update:model-value="onDatesChange"
        />
      </div>

      <div
        v-else-if="isCustomRange"
        class="filter-editor__calendar"
      >
        <hr-date-range
          :model-value="calendarRange"
          mask="YYYY-MM-DD"
          @update:model-value="onRangeChange"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { computed } from 'vue';

import { useDateRanges } from 'src/composables/dateRanges';
import { DATE_FILTER_OPERATORS } from 'src/constants/filters';
import { HrDate, HrDateRange, HrFilterChipGroup, HrListSelect, HrTextField } from 'src/design-system';

import { getDateOperatorOptions, getPeriodPresetOptions, getPeriodPresetScopes } from '../criteriaTypes';
import { CUSTOM_DATES, CUSTOM_RANGE } from '../filterStatement';
import {
  fromCalendarDates, fromCalendarRange, fromDateOperator, fromDatePreset, toCalendarDates, toCalendarRange,
} from '../dateStatement';

/**
 * Date filter: a named period, dates the user picks or a cutoff number of weeks.
 */
defineOptions({ name: 'DateEditor' });

const props = defineProps({
  /** The criterion being edited. */
  criterion: {
    type: Object,
    required: true,
  },
  /** The statement being built for it. */
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

/*
 * Scopes come from the criterion: only some filters make sense running forwards, so a criterion
 * opts into future presets rather than every date filter offering them.
 */
const { periodPresets } = useDateRanges(getPeriodPresetScopes(props.criterion));

const presetOptions = computed(() => getPeriodPresetOptions(props.criterion, periodPresets.value));

const dateOperatorOptions = getDateOperatorOptions();

const hasDateOperators = computed(() => Boolean(props.criterion.options?.dateOperators));
const dateOperator = computed(() => props.modelValue.dateOperator ?? DATE_FILTER_OPERATORS.IS_WITHIN);
const isWeeksAgo = computed(() => hasDateOperators.value && dateOperator.value === DATE_FILTER_OPERATORS.IS_MORE_THAN);

const isCustomDates = computed(() => props.modelValue.filterCriteriaType === CUSTOM_DATES);
const isCustomRange = computed(() => props.modelValue.filterCriteriaType === CUSTOM_RANGE);

const calendarDates = computed(() => toCalendarDates(props.modelValue));
const calendarRange = computed(() => toCalendarRange(props.modelValue));

// The masked field deals in strings, so an unset count is an empty one rather than null.
const weeks = computed(() => (Number.isInteger(props.modelValue.filteredItems)
  ? String(props.modelValue.filteredItems)
  : ''));

function patch(changes) {
  emit('update:modelValue', { ...props.modelValue, ...changes });
}

/*
 * A named preset carries its own resolved bounds, so choosing one replaces whatever the calendar
 * had. The custom presets carry none, which clears the bounds until the user picks dates.
 */
function onPresetChange(value) {
  const preset = periodPresets.value.find((option) => option.id === value);

  patch({
    filterCriteriaType: value,
    dateOperator: null,
    ...fromDatePreset(preset),
  });
}

function onDatesChange(value) {
  patch(fromCalendarDates(value));
}

function onRangeChange(value) {
  patch(fromCalendarRange(value));
}

function onDateOperatorChange(value) {
  patch(fromDateOperator(value));
}

function onWeeksChange(value) {
  const parsed = Number(value);

  patch({ filteredItems: value === '' || !Number.isInteger(parsed) ? null : parsed });
}
</script>

<style lang="scss" scoped>
.filter-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-editor__calendar {
  display: flex;
  justify-content: center;
}
</style>
